import { RefreshCw, Trash2, AlertTriangle, Clock } from 'lucide-react';
import Modal from './Modal';
import { getQueue, getFailed, retryFailed, discardFailed } from '../lib/outbox';

// Shows what the app still owes the server, and anything it gave up on.
//
// A write that the server refuses outright (a sale for a product that has since
// been deleted, say) cannot be retried into success, so the outbox sets it
// aside. Without somewhere to see that, the money would simply never appear in
// the takings and nobody would know why. This is that somewhere: the
// shopkeeper can retry a write once the underlying problem is fixed, or
// discard it deliberately.

export default function OutboxPanel({ isOpen, onClose, onChanged }) {
  const pending = getQueue();
  const failed = getFailed();

  const handleRetry = (key) => {
    retryFailed(key);
    onChanged?.();
  };

  const handleDiscard = (key) => {
    discardFailed(key);
    onChanged?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Unsynced work"
      titleNp="पठाउन बाँकी"
    >
      {failed.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <h3 className="font-semibold text-red-700">
              Rejected / अस्वीकृत ({failed.length})
            </h3>
          </div>
          <p className="text-xs text-gray-500 mb-3 font-nepali">
            सर्भरले अस्वीकार गर्‍यो — हेरेर पुनः पठाउनुहोस् वा हटाउनुहोस्
          </p>
          <div className="space-y-2">
            {failed.map((entry) => (
              <div
                key={entry.key}
                className="p-3 rounded-lg border border-red-200 bg-red-50"
                data-testid={`failed-entry-${entry.key}`}
              >
                <p className="font-medium text-[#2D2D2D]">
                  {entry.label || entry.path}
                </p>
                <p className="text-xs text-red-700 mt-0.5">{entry.reason}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => handleRetry(entry.key)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-sm"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Retry
                  </button>
                  <button
                    onClick={() => handleDiscard(entry.key)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-sm text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Discard
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-4 h-4 text-amber-600" />
        <h3 className="font-semibold text-[#2D2D2D]">
          Waiting / पर्खिँदै ({pending.length})
        </h3>
      </div>

      {pending.length === 0 ? (
        <p className="text-sm text-gray-500 font-nepali">
          सबै पठाइयो — केही बाँकी छैन
        </p>
      ) : (
        <div className="space-y-2">
          {pending.map((entry) => (
            <div
              key={entry.key}
              className="p-3 rounded-lg border border-gray-200 flex items-center justify-between"
            >
              <div>
                <p className="font-medium text-[#2D2D2D]">
                  {entry.label || entry.path}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(entry.queuedAt).toLocaleString()}
                </p>
              </div>
              {entry.attempts > 0 && (
                <span className="text-xs text-gray-400">
                  {entry.attempts} tr{entry.attempts === 1 ? 'y' : 'ies'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
